/* ------------------------------------------------------------------
   AIBulkChatPage.js — AI Studio → AI Post Creator chat.
   The guided chat (POST /api/ai/bulk/chat, backend/services/guidedChat.js)
   decides each next question; this page shows it with its quick-reply
   buttons, pulls links, handles the photo picker and, once everything is
   in, shows the server's `summary` as a confirm card: Edit sends the chat
   back to that question, "Create my posts" calls the SAME
   /api/ai/bulk/generate the form wizard uses and hands off to the shared
   Review/Schedule screens (/dashboard/studio/bulk/:jobId).
   For AI photos the card also shows the "Photo description" (the chat's
   "What should your photos show?", T40), sent to /generate as imageBrief.
   Listings (T41): the chat offers Redfin / upload / Zillow-or-Realtor.com
   buttons; an upload button also opens the photo picker, and after every
   link pull the chat is told how many photos came back (`linkPull`), so a
   blocked Zillow / Realtor.com link gets its friendly fallback.
   Shown by AIBulkStudioPage's Chat / Step-by-step form switch.
------------------------------------------------------------------ */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Magic, Send, ChatDotsFill, Paperclip, PencilSquare } from 'react-bootstrap-icons';
import PrefillPill from './PrefillPill';
import { UPLOAD_CHOICES } from './listingLinks';

const ax = axios.create({ withCredentials: true });
const URL_RE = /\bhttps?:\/\/[^\s]+/i;
const LINK_GOALS = ['listing', 'product'];   // goals the chat offers a link for (services/guidedChecklist.js)
const AI_COUNTS = [1, 2, 3, 4, 5, 6];        // AI photos: up to 6 per batch, as in the form
const PHOTO_COUNTS = [1, 2, 3, 4, 5, 6, 8, 10];

const OPENING = 'Hi! I’ll write a batch of posts for you. What are the posts for?';

// What goes to the server: a message's `sendAs` (e.g. an Edit tap's
// "[System note: …]", which the engine never reads as an answer) or its text.
const toPayload = (msgs) => msgs.map(({ role, content, sendAs }) => ({ role, content: sendAs || content }));
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/* After a sign-up from the name generators (T24): the chat starts with the
   prefilled answers already in `collected` and says so; the engine then asks
   for whatever is still missing, as it always does. */
const PREFILL_NAMES = { accountName: 'account name', vibe: 'vibe', topics: 'what you post about', audience: 'who you want to reach' };
export function prefillOpening(prefill) {
  const got = Object.entries(prefill.fields).map(([k, v]) => `${PREFILL_NAMES[k] || k}: ${v}`);
  return `Welcome! I’ve filled in what you made on the ${prefill.source}: ${got.join('; ')}. You can change any of it before I create your posts.`;
}

export default function AIBulkChatPage({ prefill = null }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState(() => [{ role: 'assistant', content: prefill ? prefillOpening(prefill) : OPENING }]);
  const [collected, setCollected] = useState(() => (prefill ? { goal: prefill.goal, fields: { ...prefill.fields } } : {}));
  const [goalLabels, setGoalLabels] = useState({});            // goal key → label, for the prefill pill
  const [summary, setSummary] = useState(null);                // server `summary` once everything is in
  const [pulledImages, setPulledImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);        // tappable option buttons for the last question
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [userId, setUserId] = useState(null);

  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const pickerRef = useRef(null);
  const collectedRef = useRef(collected); collectedRef.current = collected;
  const imagesRef = useRef(pulledImages); imagesRef.current = pulledImages;
  const selectedRef = useRef(selectedImages); selectedRef.current = selectedImages;
  const messagesRef = useRef(messages); messagesRef.current = messages;
  const userIdRef = useRef(userId); userIdRef.current = userId;

  // When new photos arrive, select them all by default (user can deselect).
  function addImages(urls) {
    const images = [...new Set([...imagesRef.current, ...urls])];
    setPulledImages(images);
    setSelectedImages(prev => [...new Set([...prev, ...urls])]);
    return images;
  }
  const toggleSelect = (u) => setSelectedImages(prev => prev.includes(u) ? prev.filter(x => x !== u) : [...prev, u]);

  useEffect(() => {
    ax.get('/api/auth/me').then(me => { if (me.data?.user?.id) setUserId(me.data.user.id); }).catch(() => {});
    // The opening question's buttons: the goals, as the engine itself offers them.
    ax.get('/api/ai/bulk/goals').then(({ data }) => {
      const goals = data.goals || [];
      setGoalLabels(Object.fromEntries(goals.map(g => [g.key, g.label])));
      if (!prefill && messagesRef.current.length === 1) setQuickReplies(goals.map(g => g.label));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefilled: the first question is the engine's (the first thing still missing).
  const startedRef = useRef(false);
  useEffect(() => {
    if (!prefill || startedRef.current) return;
    startedRef.current = true;
    runTurn(messagesRef.current, { ...collectedRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && el.scrollTo) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, busy, generating, summary]);

  /* One chat turn: post the transcript + brief, show the reply. */
  async function runTurn(nextMsgs, working, fallbackError = 'Something went wrong — please try again.', extra = {}) {
    setMessages(nextMsgs);
    setBusy(true); setError(''); setQuickReplies([]); setSummary(null);
    try {
      const { data } = await ax.post('/api/ai/bulk/chat', {
        messages: toPayload(nextMsgs), collected: working, photosAvailable: imagesRef.current.length, ...extra,
      });
      setCollected(data.collected || working);
      setMessages(ms => [...ms, { role: 'assistant', content: data.message }]);
      setQuickReplies(data.options || []);
      setSummary(data.summary || null);
    } catch (e) {
      setCollected(working);
      if (e.response?.status === 402) {
        setMessages(ms => [...ms, { role: 'assistant', content: e.response.data?.message || 'You’ve used all your AI credits for this cycle. Upgrade your plan for more.' }]);
      } else {
        setError(e.response?.data?.message || fallbackError);
      }
    } finally {
      setBusy(false);
    }
  }

  async function uploadOne(file) {
    const fd = new FormData();
    fd.append('files', file); fd.append('userId', userIdRef.current || ''); fd.append('prefix', 'ai-bulk');
    const { data } = await ax.post('/api/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data?.files?.[0]?.url || null;
  }

  // Photos picked while a chat turn is still running (an "Upload photos"
  // button sends its answer and opens the picker at once) wait for it.
  const pendingFilesRef = useRef(null);
  useEffect(() => {
    if (busy || !pendingFilesRef.current) return;
    const files = pendingFilesRef.current; pendingFilesRef.current = null;
    uploadFiles(files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);

  function onAttach(e) {
    const files = [...(e.target.files || [])]; e.target.value = '';
    if (!files.length || generating) return;
    if (busy) { pendingFilesRef.current = files; return; }
    uploadFiles(files);
  }

  async function uploadFiles(files) {
    setBusy(true); setError(''); setQuickReplies([]);
    const nextMsgs = [...messagesRef.current, { role: 'user', content: `📎 Uploaded ${plural(files.length, 'photo')}` }];
    setMessages(nextMsgs);
    const urls = [];
    for (const f of files) { try { const u = await uploadOne(f); if (u) urls.push(u); } catch { /* skip */ } }
    if (!urls.length) { setError('Photo upload failed — please try again.'); setBusy(false); return; }
    addImages(urls);
    await runTurn(nextMsgs, { ...collectedRef.current }, 'Photo upload failed — please try again.');
  }

  async function send(textArg) {
    const text = (typeof textArg === 'string' ? textArg : input).trim();
    if (!text || busy || generating) return;
    setInput(''); setError(''); setQuickReplies([]);
    const nextMsgs = [...messagesRef.current, { role: 'user', content: text }];
    setMessages(nextMsgs);
    setBusy(true);
    let working = { ...collectedRef.current };

    // A link in the message → pull details + photos (Redfin for listings, any site otherwise),
    // one attempt; the chat hears how many photos came back (T41).
    const m = text.match(URL_RE);
    let extra = {};
    if (m) {
      let photos = 0;
      try {
        const { data } = await ax.post('/api/ai/bulk/url-preview', { url: m[0], goal: working.goal || '' });
        if (data.suggested && Object.keys(data.suggested).length) working = { ...working, fields: { ...(working.fields || {}), ...data.suggested } };
        if (Array.isArray(data.images) && data.images.length) { addImages(data.images); photos = data.images.length; }
      } catch { /* the chat carries on without the pull */ }
      extra = { linkPull: { url: m[0], photos } };
    }
    await runTurn(nextMsgs, working, undefined, extra);
  }

  // A quick-reply button. The listing's upload choices also open the photo picker (T41).
  function tapOption(opt) {
    if (UPLOAD_CHOICES.includes(opt) && fileRef.current) fileRef.current.click();
    send(opt);
  }

  // User picked their photos + strategy from the in-chat picker.
  async function choosePhotos(strategy) {
    if (busy || generating) return;
    const sel = (selectedRef.current.length ? selectedRef.current : imagesRef.current);
    if (!sel.length) { setError('Select at least one photo first.'); return; }
    const label = strategy === 'ai'
      ? `Use AI-enhanced photos based on my ${plural(sel.length, 'selected photo')}`
      : `Use my ${plural(sel.length, 'selected photo')} as-is`;
    await runTurn([...messagesRef.current, { role: 'user', content: label }], { ...collectedRef.current, imageStrategy: strategy });
  }

  /* Edit on the summary card: forget that one answer and let the engine
     ask for it again (it always asks the first missing item). */
  async function editItem(item, label, was) {
    if (busy || generating) return;
    if (item === 'image' && imagesRef.current.length) {
      // With photos the choice lives in the photo picker, not in a question.
      if (pickerRef.current && pickerRef.current.scrollIntoView) pickerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }
    const prev = collectedRef.current;
    const working = { ...prev, fields: { ...(prev.fields || {}) }, skipped: [...(prev.skipped || [])] };
    if (item === 'goal') { delete working.goal; delete working.link; delete working.linkBlocked; working.fields = {}; working.skipped = []; }
    else if (item === 'link') { delete working.link; delete working.linkBlocked; working.skipped = working.skipped.filter(s => s !== 'link'); }
    else if (item === 'image') delete working.imageStrategy;
    else if (item === 'imageBrief') {
      // Ask "What should your photos show?" again; the photos are AI ones.
      delete working.imageBrief; delete working.fields.imageBrief;
      working.skipped = working.skipped.filter(s => s !== 'imageBrief');
      if (working.imageStrategy !== 'upload') working.imageStrategy = 'ai';
    } else if (item.startsWith('field:')) {
      const key = item.slice('field:'.length);
      delete working.fields[key];
      working.skipped = working.skipped.filter(s => s !== key);
    }
    delete working.retry;
    const shown = `✏️ Change ${label.toLowerCase()}${was ? ` (was “${was}”)` : ''}`;
    await runTurn([...messagesRef.current, { role: 'user', content: shown, sendAs: `[System note: the user tapped Edit to change "${label}".]` }], working);
  }

  const chooseCount = (n) => setCollected(c => ({ ...c, count: n }));

  async function generate(brief) {
    setGenerating(true); setError('');
    const selected = selectedRef.current.length ? selectedRef.current : imagesRef.current;
    let strat = brief.imageStrategy === 'upload' ? 'upload' : 'ai';
    if (strat === 'upload' && !selected.length) strat = 'ai';   // can't "use my photos" with none
    // The chat keeps a copy of the photo description in `fields` for clients
    // that send fields as-is; this page sends it on its own.
    const { imageBrief: _briefCopy, ...fields } = brief.fields || {};
    try {
      const { data } = await ax.post('/api/ai/bulk/generate', {
        goal: brief.goal,
        count: brief.count || 4,
        source: fields,
        ...(strat === 'ai' && brief.imageBrief ? { imageBrief: brief.imageBrief } : {}),
        language: brief.language || 'English',
        emojis: true,
        hashtags: { enabled: true, count: 3 },
        imageStrategy: strat,
        uploadedImageUrls: strat === 'upload' ? selected : [],
        referenceImageUrls: selected,
      });
      if (data.jobId) { navigate(`/dashboard/studio/bulk/${data.jobId}`); return; }
      setError('Generation failed — please try again.');
      setGenerating(false);
    } catch (e) {
      setGenerating(false);
      if (e.response?.data?.error === 'image_limit' || e.response?.status === 402) {
        setMessages(ms => [...ms, { role: 'assistant', content: "You're out of bulk posts for this month. Upgrade your plan to keep creating — you'll find it under Plans & Billing." }]);
      } else {
        setError('Generation failed — please try again.');
      }
    }
  }

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <ChatDotsFill color="#4f46e5" /> AI Post Creator
        </h2>
        <p style={{ fontSize: 13.5, color: '#6b7280', margin: '4px 0 0' }}>
          Chat with the assistant — it asks what it needs, shows you a summary, then builds a batch of posts to review.
        </p>
        {prefill && collected.goal === prefill.goal && goalLabels[prefill.goal] && (
          <div style={{ marginTop: 10 }}><PrefillPill prefill={prefill} goalLabel={goalLabels[prefill.goal]} /></div>
        )}
      </div>

      {/* Chat window */}
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 16, background: '#fff', display: 'flex', flexDirection: 'column', height: '64vh', minHeight: 420, overflow: 'hidden' }}>
        <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {messages.map((m, i) => <Bubble key={i} role={m.role} text={m.content} />)}
          {quickReplies.length > 0 && !busy && !generating && (
            <div style={{ alignSelf: 'flex-start', display: 'flex', gap: 8, flexWrap: 'wrap', maxWidth: '90%' }}>
              {quickReplies.map((opt, i) => (
                <button key={i} onClick={() => tapOption(opt)}
                  style={{ padding: '8px 14px', borderRadius: 999, border: '1px solid #4f46e5', background: '#fff', color: '#4f46e5', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  {opt}
                </button>
              ))}
            </div>
          )}
          {pulledImages.length > 0 && !generating && (
            <div ref={pickerRef} style={{ alignSelf: 'stretch', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 14, padding: 12 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#111827', marginBottom: 2 }}>
                {plural(pulledImages.length, 'photo')} available
              </div>
              <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 8 }}>Tap to choose which to use ({selectedImages.length} selected), then pick how to use them.</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                {pulledImages.map((u, i) => {
                  const on = selectedImages.includes(u);
                  return (
                    <button key={i} onClick={() => toggleSelect(u)} disabled={busy}
                      style={{ position: 'relative', width: 52, height: 64, padding: 0, borderRadius: 8, overflow: 'hidden', cursor: busy ? 'default' : 'pointer', border: on ? '2px solid #4f46e5' : '2px solid #e5e7eb', background: 'none' }}>
                      <img src={u} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: on ? 1 : 0.55 }} />
                      {on && <span style={{ position: 'absolute', top: 2, right: 2, background: '#4f46e5', color: '#fff', borderRadius: '50%', width: 15, height: 15, fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</span>}
                    </button>
                  );
                })}
                <label title="Upload more" style={{ width: 52, height: 64, borderRadius: 8, border: '2px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#94a3b8' }}>
                  <Paperclip size={15} />
                  <input type="file" accept="image/*" multiple onChange={onAttach} style={{ display: 'none' }} />
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => choosePhotos('upload')} disabled={busy || !selectedImages.length}
                  style={{ flex: '1 1 0', minWidth: 150, padding: '9px 12px', borderRadius: 10, border: '1px solid #4f46e5', background: collected.imageStrategy === 'upload' ? '#eef2ff' : '#fff', color: '#4f46e5', fontWeight: 600, fontSize: 13, cursor: busy || !selectedImages.length ? 'default' : 'pointer' }}>
                  Use these as-is
                </button>
                <button onClick={() => choosePhotos('ai')} disabled={busy || !selectedImages.length}
                  style={{ flex: '1 1 0', minWidth: 150, padding: '9px 12px', borderRadius: 10, border: 'none', background: busy || !selectedImages.length ? '#c7d2fe' : '#4f46e5', color: '#fff', fontWeight: 600, fontSize: 13, cursor: busy || !selectedImages.length ? 'default' : 'pointer' }}>
                  ✨ AI-enhanced from these
                </button>
              </div>
            </div>
          )}
          {summary && !busy && !generating && (
            <SummaryCard summary={summary} collected={collected}
              photos={pulledImages.length} selected={selectedImages.length}
              onEdit={editItem} onCount={chooseCount} onCreate={() => generate(collectedRef.current)} />
          )}
          {(busy || generating) && <Bubble role="assistant" text={generating ? 'Creating your posts…' : '…'} typing />}
        </div>

        {/* Input */}
        <div style={{ borderTop: '1px solid #f1f5f9', padding: 12, background: '#fafafa' }}>
          {error && <div style={{ fontSize: 12, color: '#b91c1c', marginBottom: 6 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onAttach} style={{ display: 'none' }} />
            <button
              onClick={() => fileRef.current && fileRef.current.click()}
              disabled={busy || generating}
              title="Upload photos"
              style={{ flexShrink: 0, width: 44, height: 44, borderRadius: 12, border: '1px solid #d1d5db', cursor: busy || generating ? 'default' : 'pointer', background: '#fff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Paperclip size={18} />
            </button>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={generating ? 'Creating your posts…' : 'Type your answer…'}
              aria-label="Your answer"
              disabled={generating}
              rows={1}
              style={{ flex: 1, resize: 'none', border: '1px solid #d1d5db', borderRadius: 12, padding: '11px 13px', fontSize: 14, lineHeight: 1.4, maxHeight: 120, outline: 'none', background: generating ? '#f3f4f6' : '#fff' }}
            />
            <button
              onClick={send}
              disabled={busy || generating || !input.trim()}
              style={{ flexShrink: 0, width: 44, height: 44, borderRadius: 12, border: 'none', cursor: busy || generating || !input.trim() ? 'default' : 'pointer', background: busy || generating || !input.trim() ? '#c7d2fe' : '#4f46e5', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              title="Send"
            >
              {generating ? <Magic size={18} /> : <Send size={17} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* What the posts' images will be, in plain words. */
function photosText(strategy, photos, selected) {
  if (photos) {
    const n = selected || photos;
    return strategy === 'upload' ? `Your ${plural(n, 'photo')}, as they are` : `AI images based on your ${plural(n, 'photo')}`;
  }
  if (strategy === 'upload') return 'Your own photos — none added yet. Tap 📎 to add them, or AI will create the images.';
  return 'Created with AI';
}

/* The confirm card: every answer, an Edit for each, the post count and
   "Create my posts". `summary` comes from the server (guidedChat.summaryOf). */
function SummaryCard({ summary, collected, photos, selected, onEdit, onCount, onCreate }) {
  const strategy = collected.imageStrategy || summary.imageStrategy;
  const usesAI = strategy !== 'upload' || !photos;
  const counts = usesAI ? AI_COUNTS : PHOTO_COUNTS;
  const count = counts.includes(collected.count) ? collected.count : null;
  // The chat can stop on the card with a required answer still missing (it
  // never asks one thing more than 3 times): that answer comes first.
  const missing = (summary.items || []).filter(it => !it.optional && !it.value);
  const rows = [
    { item: 'goal', label: 'Posts for', value: summary.goal?.label },
    ...(summary.items || []).map(it => ({
      item: `field:${it.key}`, label: it.label, value: it.value, skipped: it.skipped || (it.optional && !it.value),
      empty: !it.optional && !it.value ? 'Still needed' : undefined,
    })),
  ];
  const canCreate = !!count && !missing.length;
  if (LINK_GOALS.includes(summary.goal?.key)) rows.push({ item: 'link', label: 'Link', value: summary.link, skipped: !summary.link, empty: 'No link' });
  rows.push({ item: 'image', label: 'Images', value: photosText(strategy, photos, selected) });
  // What the AI photos should show (T40); none = the AI decides. Not when
  // the customer chose their own photos (even before adding them).
  if (usesAI && strategy !== 'upload') {
    const brief = summary.imageBrief || collected.imageBrief;
    rows.push({ item: 'imageBrief', label: 'Photo description', value: brief, skipped: !brief, empty: 'You decide' });
  }
  if (collected.language && collected.language !== 'English') rows.push({ label: 'Language', value: collected.language });

  return (
    <div data-testid="chat-summary" style={{ alignSelf: 'stretch', background: '#fff', border: '1px solid #c7d2fe', borderRadius: 14, padding: 14, boxShadow: '0 1px 3px rgba(79,70,229,0.08)' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 2 }}>Here’s what I’ve got</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>Check your answers. Tap Edit to change one.</div>
      <div>
        {rows.map((r, i) => (
          <div key={r.item || r.label} data-testid={r.item ? `summary-${r.item}` : undefined}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0', borderTop: i ? '1px solid #f1f5f9' : 'none' }}>
            <div style={{ width: 150, flexShrink: 0, fontSize: 12.5, fontWeight: 600, color: '#6b7280' }}>{r.label}</div>
            <div style={{ flex: 1, fontSize: 13.5, color: r.value ? '#111827' : r.empty && !r.skipped ? '#b91c1c' : '#9ca3af', fontStyle: r.value ? 'normal' : 'italic', wordBreak: 'break-word' }}>
              {r.value || (r.skipped ? (r.empty || 'Skipped') : (r.empty || '—'))}
            </div>
            {r.item && (
              <button onClick={() => onEdit(r.item, r.label, r.value)} aria-label={`Edit ${r.label}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 7, border: '1px solid #d1d5db', background: '#fff', color: '#4f46e5', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                <PencilSquare size={11} /> Edit
              </button>
            )}
          </div>
        ))}
      </div>
      <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10, marginTop: 4 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
          How many posts should I create?{usesAI ? ' (AI photos: up to 6)' : ''}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {counts.map(n => (
            <button key={n} onClick={() => onCount(n)} aria-pressed={count === n} aria-label={plural(n, 'post')}
              style={{ width: 44, height: 40, borderRadius: 10, border: '1px solid #4f46e5', background: count === n ? '#4f46e5' : '#fff', color: count === n ? '#fff' : '#4f46e5', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              {n}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 14 }}>
        {missing.length > 0
          ? <span style={{ fontSize: 12, color: '#b91c1c' }}>Add the {missing.map(it => it.label.toLowerCase()).join(' and ')} first (tap Edit).</span>
          : !count && <span style={{ fontSize: 12, color: '#9ca3af' }}>Pick how many posts first.</span>}
        <button onClick={onCreate} disabled={!canCreate}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: canCreate ? 'pointer' : 'not-allowed', opacity: canCreate ? 1 : 0.5 }}>
          <Magic size={15} /> Create my posts
        </button>
      </div>
    </div>
  );
}

function Bubble({ role, text, typing }) {
  const isUser = role === 'user';
  return (
    <div style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
      <div style={{
        background: isUser ? '#4f46e5' : '#f3f4f6',
        color: isUser ? '#fff' : '#111827',
        borderRadius: 14,
        borderBottomRightRadius: isUser ? 4 : 14,
        borderBottomLeftRadius: isUser ? 14 : 4,
        padding: '10px 13px',
        fontSize: 14,
        lineHeight: 1.45,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontStyle: typing ? 'italic' : 'normal',
        opacity: typing ? 0.7 : 1,
      }}>
        {text}
      </div>
    </div>
  );
}
